import React, { memo, useEffect, useRef } from 'react';
import { TrialInputProps } from '~/common/types';
import { Input } from '~/common/components/Input';
import {
  validateString,
  validateEmail,
  validatePhone,
  validateFullName,
  validateRadio,
} from '~/common/util/customValidation';
import { FastField, Field } from 'formik';
import styles from '../Grid.module.css';
import { trackBlur, trackRadioEvent } from '~/common/util/trackBlur';
import { RadioFieldGroup } from '~/common/components/RadioFieldGroup';
import { useAppStore } from '~/common/store/appStore';
import { useShallow } from 'zustand/react/shallow';
import { MODIFIER_TYPE } from '~/common/consts';

const BusinessInputComponent = (props: TrialInputProps) => {
  const {
    formikTouched,
    formikErrors,
    inputs,
    showHint,
    trackErrorBlur,
    validateBusinessEmail,
    existingAccount,
    userEmail,
    existingEmailError,
    step,
    zoomInfoData,
  } = props;

  const { fullName, email, temp_phone__c, title, company, employees } = inputs;

  const { modifier, emailPlaceholder } = useAppStore(
    useShallow((state) => ({ modifier: state.modifier, emailPlaceholder: state.emailPlaceholder }))
  );

  const hero = modifier === MODIFIER_TYPE.HERO;
  const zoomHasName = !!(zoomInfoData?.hasFirstName && zoomInfoData?.hasLastName);
  const zoomHasTitle = !!zoomInfoData?.hasJobTitle;
  const zoomHasCompany = !!zoomInfoData?.hasCompanyName;
  const zoomHasEmployees = !!zoomInfoData?.hasCompanyEmployeeCount;

  const nameRef = useRef(null);

  useEffect(() => {
    if (step === 2 && !zoomHasName && nameRef.current) {
      nameRef.current.focus();
    }
  }, [step]);

  return (
    <div className={`${styles.inputGrid} ${styles.trial} ${hero ? styles.hero : ''}`}>
      {step === 1 && !userEmail ? (
        <Field
          name={email.id}
          type={'email'}
          error={formikErrors[email.id] && formikTouched[email.id]}
          errormessage={existingAccount ? existingEmailError : email.error}
          showHint={showHint}
          hintmessage={hero && emailPlaceholder ? emailPlaceholder : email.placeHolder}
          validate={validateEmail}
          as={Input}
          label={hero ? null : email.label}
          maxLength={128}
          onFocus={() => {
            trackBlur(email.id);
          }}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            validateBusinessEmail(event.target.value);
            trackErrorBlur(email.id, validateEmail, event.target.value);
          }}
          autoFocus={hero ? false : true}
          placeHolder={hero ? emailPlaceholder || email.placeHolder : null}
        />
      ) : (
        <>
          {!zoomHasName && (
            <FastField
              name={fullName.id}
              type={'text'}
              error={formikErrors[fullName.id] && formikTouched[fullName.id]}
              errormessage={fullName.error}
              validate={validateFullName}
              as={Input}
              label={fullName.label}
              onFocus={() => {
                trackBlur(fullName.id);
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                trackErrorBlur(fullName.id, validateFullName, event.target.value);
              }}
              autoFocus={true}
              inputRef={nameRef}
            />
          )}
          {temp_phone__c && (
            <FastField
              name={temp_phone__c.id}
              type={'tel'}
              error={formikErrors[temp_phone__c.id] && formikTouched[temp_phone__c.id]}
              errormessage={temp_phone__c.error}
              as={Input}
              label={temp_phone__c.label}
              validate={temp_phone__c.required ? validatePhone : ''}
              maxLength={30}
              onFocus={() => {
                trackBlur(temp_phone__c.id);
              }}
            />
          )}
          {title && !zoomHasTitle && (
            <Field
              name={title.id}
              type={'text'}
              error={formikErrors[title.id] && formikTouched[title.id]}
              errormessage={title.error}
              validate={validateString}
              as={Input}
              label={title.label}
              onFocus={() => {
                trackBlur(title.id);
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                trackErrorBlur(title.id, validateString, event.target.value);
              }}
            />
          )}
          {company && !zoomHasCompany && (
            <FastField
              name={company.id}
              type={'text'}
              error={formikErrors[company.id] && formikTouched[company.id]}
              errormessage={company.error}
              validate={validateString}
              as={Input}
              label={company.label}
              onFocus={() => {
                trackBlur(company.id);
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                trackErrorBlur(company.id, validateString, event.target.value);
              }}
            />
          )}
          {employees && !zoomHasEmployees && (
            <RadioFieldGroup
              name={employees.id}
              options={employees.options}
              label={employees.label}
              error={formikErrors[employees.id] && formikTouched[employees.id]}
              errorMessage={employees.error}
              onFocus={() => {
                trackBlur(employees.id);
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                trackErrorBlur(employees.id, validateRadio, event.target.checked ? event.target.value : '');
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export const BusinessInput = memo(BusinessInputComponent);
